import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';
import { PROGRAM_MAPPING } from '../../../utils/dataProcessor/constants';

export const InnovationCollaborationChart = memo(function InnovationCollaborationChart() {
  const { internalCollaboration, totalCount, innovationResults } = useInnovationData();
  const programYAxisWidth = useResponsiveYAxisWidth(50);
  const centerYAxisWidth = useResponsiveYAxisWidth(80);
  if (!internalCollaboration || totalCount === 0) return (
    <ChartWrapper title="Innovation Collaboration">
      <NoChartData message="No collaboration data available" detail="This chart shows internal cross-center and cross-program collaboration using the Contributing Initiatives and Contributing Centers fields on innovation results." />
    </ChartWrapper>
  );

  const { programContributions, centerContributions, multiProgramResults } = internalCollaboration;

  const programData = Object.entries(programContributions)
    .map(([code, count]) => ({
      name: code,
      fullName: PROGRAM_MAPPING[code] || code,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);

  const centerData = Object.entries(centerContributions)
    .map(([code, count]) => ({ name: code, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);

  const exportData = [
    ...programData.map(d => ({ Entity: d.fullName, Type: 'Program', 'Innovation Results': d.count })),
    ...centerData.map(d => ({ Entity: d.name, Type: 'Center', 'Innovation Results': d.count })),
  ];

  return (
    <ChartWrapper title="Internal Collaboration on Innovations" info="Internal CGIAR collaboration on innovation results: which programs and centers co-contribute to innovation outputs." chartData={exportData} exportFilename="innovation-collaboration" sourceResults={innovationResults}>
      <div className="mb-4 bg-indigo-50 rounded-lg p-3 text-center">
        <span className="text-sm text-indigo-700">
          <strong>{multiProgramResults}</strong> of {totalCount} innovation results ({totalCount > 0 ? ((multiProgramResults / totalCount) * 100).toFixed(1) : 0}%) involve multiple programs
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Programs Contributing to Innovations</h5>
          <ResponsiveContainer width="100%" height={Math.max(200, programData.length * 28)}>
            <BarChart data={programData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={programYAxisWidth} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.fullName || d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Centers Contributing to Innovations</h5>
          <ResponsiveContainer width="100%" height={Math.max(200, centerData.length * 28)}>
            <BarChart data={centerData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={centerYAxisWidth} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartWrapper>
  );
});
